/**
 * @fileoverview Edit the shared set of employee workspaces with a revisioned backend contract. Saved settings and unsaved selections stay distinct; administrators retain their own availability. `kc-internal-frontend/src/AppAccessContext.js` consumes accepted choices at route boundaries. This screen changes navigation availability rather than granting every resource operation.
 */

import React, { useCallback, useEffect, useState } from 'react'
import api from '../../api'
import { AppButton, AppIcon, AppSpinner, AppToggle } from '../shared'
import './EmployeeAccess.css'

 
export default function EmployeeAccess() {
    const [saved, setSaved] = useState(null)
    const [selected, setSelected] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [notice, setNotice] = useState('')

    const load = useCallback( 
    async () => {
        setLoading(true)
        setError('')
        setNotice('')
        try {
            const { data } = await api.get('/admin/employee-access')
            setSaved(data)
            setSelected(data.enabled_sections)
        } catch (_error) {
            setError('Employee access could not be loaded. Try again.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { load() }, [load])

    const dirty = saved && (selected.length !== saved.enabled_sections.length || selected.some((id) => !saved.enabled_sections.includes(id)))

     
    const save = async () => {
        setSaving(true)
        setError('')
        setNotice('')
        try {
            const { data } = await api.put('/admin/employee-access', { enabled_sections: selected, revision: saved.revision })
            setSaved(data)
            setSelected(data.enabled_sections)
            setNotice('Employee access saved.')
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Employee access could not be saved. Your changes are still here.')
        } finally {
            setSaving(false)
        }
    }

    if (loading) return <div className="employee-access employee-access--loading"><AppSpinner /></div>
    if (!saved) return <div className="employee-access"><p role="alert">{error}</p><AppButton label="Try again" onClick={load} /></div>

    return (
        <div className="employee-access">
            <div className="employee-access__heading">
                <div>
                    <h2>Employee access</h2>
                    <p>Choose what employees can open. Admins keep full access, and personal settings stay available.</p>
                </div>
                <AppButton label={saving ? 'Saving…' : 'Save changes'} icon="pi pi-check" onClick={save} disabled={!dirty || saving} />
            </div>
            <div className="employee-access__toolbar">
                <span>{selected.length} of {saved.sections.length} sections available</span>
                <div>
                    <AppButton label="Select all" variant="secondary" disabled={saving || selected.length === saved.sections.length} onClick={() => { setSelected(saved.sections.map(({ id }) => id)); setNotice('') }} />
                    <AppButton label="Clear all" variant="secondary" disabled={saving || selected.length === 0} onClick={() => { setSelected([]); setNotice('') }} />
                    {dirty && <AppButton label="Discard changes" variant="secondary" disabled={saving} onClick={() => { setSelected(saved.enabled_sections); setError(''); setNotice('') }} />}
                </div>
            </div>
            {error && <div className="employee-access__error" role="alert">{error} <AppButton label="Reload saved settings" variant="secondary" onClick={load} disabled={saving} /></div>}
            <div className="employee-access__notice" role="status">{dirty ? 'Unsaved changes' : notice}</div>
            <div className="employee-access__groups">
                {[...new Set(saved.sections.map(({ group }) => group))].map((group) => (
                    <section className="employee-access__group" key={group} aria-label={group}>
                        <h3>{group}</h3>
                        {saved.sections.filter((section) => section.group === group).map(({ id, label, icon }) => (
                            <div className="employee-access__row" key={id}>
                                <AppIcon icon={icon} />
                                <label htmlFor={`employee-access-${id}`}>{label}</label>
                                <AppToggle id={`employee-access-${id}`} ariaLabel={label} checked={selected.includes(id)} disabled={saving} onChange={({ value }) => {
                                    setSelected((current) => value ? [...current, id] : current.filter((key) => key !== id))
                                    setNotice('')
                                }} />
                            </div>
                        ))}
                    </section>
                ))}
            </div>
        </div>
    )
}
